import Modal from '@/src/Common/components/Modal';
import { PlusIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import useZipPhotos from './hooks/useZipPhotos';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';

export default function ZipPrompt({
    shareIdentifiers,
    activationId,
    onComplete,
}: {
    shareIdentifiers: string[];
    activationId: number;
    onComplete: () => void;
}) {
    const cancelButtonRef = useRef(null);
    const [newDestination, setNewDestination] = useState('');
    const [isCreatingZipRequest, setIsCreatingZipRequest] = useState(false);
    const { createZip } = useZipPhotos();

    const createZipAndSend = async () => {
        if (!newDestination) {
            return;
        }

        setIsCreatingZipRequest(true);

        await createZip(shareIdentifiers, newDestination, activationId);

        setIsCreatingZipRequest(false);
        toast.success('Zip request created');
        onComplete();
    };

    return (
        <Modal cancelButtonRef={cancelButtonRef} onDismiss={onComplete}>
            <div>
                <div className="bg-background px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary sm:mx-0 sm:h-10 sm:w-10">
                            <PlusIcon
                                className="h-6 w-6 text-primary-foreground"
                                aria-hidden="true"
                            />
                        </div>
                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                            <h3
                                className="text-base font-semibold text-foreground"
                            >
                                Create Zip of Selected Photos
                            </h3>
                            <div className="mt-2">
                                <p className="text-sm text-muted-foreground">
                                    Creates a zip file with{' '}
                                    {shareIdentifiers.length} photos. On
                                    completion, a link to the final zip will be
                                    sent to the email address you provide.
                                </p>
                                {shareIdentifiers.length > 5000 && (
                                    <p className="text-sm text-muted-foreground">
                                        This will be divided into{' '}
                                        {Math.ceil(
                                            shareIdentifiers.length / 5000,
                                        )}{' '}
                                        archives of 5000 photos.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="ml-12 mt-2 p-2">
                    <div className="space-y-2">
                        <Label htmlFor="destination">
                            Email
                        </Label>
                        <Input
                            id="name"
                            name="name"
                            type="email"
                            required
                            value={newDestination}
                            onChange={(e) =>
                                setNewDestination(e.target.value)
                            }
                            placeholder="Enter email address"
                        />
                    </div>
                </div>
            </div>
            <div className="bg-muted/50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-3">
                <Button
                    onClick={createZipAndSend}
                    disabled={isCreatingZipRequest}
                >
                    {isCreatingZipRequest ? 'Creating...' : 'Create & Send Zip'}
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => onComplete()}
                    ref={cancelButtonRef}
                >
                    Cancel
                </Button>
            </div>
        </Modal>
    );
}
